import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setError('No session ID found');
      setLoading(false);
      return;
    }

    try {
      const response = await api.getCheckoutStatus(sessionId);
      setStatus(response);
      setLoading(false);
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setError('Failed to verify payment');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <Card className="p-12 text-center max-w-md">
          <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            Verifying Payment...
          </h2>
          <p className="text-slate-600">
            Please wait while we confirm your subscription.
          </p>
        </Card>
      </div>
    );
  }

  if (error || status?.status === 'failed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center px-4">
        <Card className="p-12 text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            Payment Failed
          </h2>
          <p className="text-slate-600 mb-6">
            {error || 'There was an issue processing your payment. Please try again.'}
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/subscription')}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              Back to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center px-4">
      <Card className="p-12 text-center max-w-md">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-slate-900 mb-2">
          Welcome to Homeschool Tracker!
        </h2>
        <p className="text-slate-600 mb-6">
          Your 7-day free trial has started. You won't be charged until after the trial period ends.
        </p>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
          <h3 className="font-semibold text-slate-900 mb-2">What's Next?</h3>
          <ul className="space-y-2 text-sm text-slate-700">
            <li>✓ Add your children to the system</li>
            <li>✓ Create your first lesson plan</li>
            <li>✓ Set up assignments and track progress</li>
            <li>✓ Explore all the features during your trial</li>
          </ul>
        </div>

        <Button
          onClick={() => navigate('/dashboard')}
          className="w-full bg-purple-600 hover:bg-purple-700 py-6 text-lg"
        >
          Get Started →
        </Button>
        
        <p className="text-xs text-slate-500 mt-4">
          You can cancel anytime before your trial ends to avoid charges.
        </p>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
